import { useEffect, useState, useMemo } from "react";
import { api } from "../lib/api";
import { formatDate, formatIDR } from "../lib/format";
import PageHeader from "../components/PageHeader";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { MessageCircle, CheckCircle2, Phone, BedDouble, Users, Bell, Loader2, RefreshCw, Calendar } from "lucide-react";
import { whatsappReminderUrl } from "../lib/whatsapp";
import { toast } from "sonner";

const SENT_KEY_PREFIX = "pondokraja_reminder_sent_";

// localStorage helpers — auto-scoped per booking & target date
const isSent = (bookingId, dateStr) => {
  return localStorage.getItem(`${SENT_KEY_PREFIX}${bookingId}_${dateStr}`) === "1";
};
const markSent = (bookingId, dateStr, value) => {
  const k = `${SENT_KEY_PREFIX}${bookingId}_${dateStr}`;
  if (value) localStorage.setItem(k, "1");
  else localStorage.removeItem(k);
};

// Add N days to today (YYYY-MM-DD)
const addDaysISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};

const RANGE_OPTIONS = [
  { value: "0", label: "Hari ini" },
  { value: "1", label: "Besok (H-1)" },
  { value: "2", label: "2 hari lagi" },
  { value: "3", label: "3 hari lagi" },
  { value: "7", label: "7 hari ke depan" },
];

export default function Reminders() {
  const [days, setDays] = useState("1");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshTick, setRefreshTick] = useState(0);

  const targetDate = useMemo(() => addDaysISO(parseInt(days, 10)), [days]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/bookings");
      setBookings(res.data);
    } catch (e) {
      toast.error("Gagal memuat booking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Filter bookings: check_in within range
  const filtered = useMemo(() => {
    const today = addDaysISO(0);
    const numDays = parseInt(days, 10);
    let endDate;
    if (numDays === 7) {
      // Range: tomorrow -> 7 days
      endDate = addDaysISO(7);
      return bookings
        .filter((b) => b.status !== "Cancelled" && b.check_in > today && b.check_in <= endDate)
        .sort((a, b) => a.check_in.localeCompare(b.check_in));
    }
    return bookings
      .filter((b) => b.status !== "Cancelled" && b.check_in === targetDate)
      .sort((a, b) => (a.guest_name || "").localeCompare(b.guest_name || ""));
  }, [bookings, days, targetDate]);

  const sentCount = useMemo(
    () => filtered.filter((b) => isSent(b.id, targetDate)).length,
    [filtered, targetDate, refreshTick]
  );

  const handleSend = (booking) => {
    if (!booking.phone_number) {
      toast.error("Tamu ini tidak punya nomor HP");
      return;
    }
    window.open(whatsappReminderUrl(booking), "_blank");
    markSent(booking.id, targetDate, true);
    setRefreshTick((t) => t + 1);
    toast.success(`WA ke ${booking.guest_name} dibuka. Ditandai sebagai terkirim.`);
  };

  const toggleSent = (booking) => {
    const cur = isSent(booking.id, targetDate);
    markSent(booking.id, targetDate, !cur);
    setRefreshTick((t) => t + 1);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reminder H-1"
        subtitle="Kirim pesan WhatsApp pengingat ke tamu yang akan check-in. Klik tombol Kirim WA untuk membuka chat — gratis tanpa biaya API."
        actions={
          <Button
            data-testid="btn-refresh-reminders"
            onClick={load}
            variant="outline"
            className="rounded-xl border-[#EBE6DE] hover:bg-[#FDFBF7]"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      {/* Filter bar + summary */}
      <div className="bg-white rounded-2xl border border-[#EBE6DE] p-5 flex flex-wrap items-center gap-4 shadow-sm">
        <div className="flex items-center gap-3">
          <Calendar className="h-5 w-5 text-[#D16B4D]" />
          <div className="text-sm font-medium text-[#172E21]">Periode</div>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger data-testid="select-reminder-period" className="w-44 border-[#EBE6DE]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="flex-1" />

        <div className="flex items-center gap-2 text-sm">
          <div className="px-3 py-1.5 rounded-lg bg-[#FDFBF7] border border-[#EBE6DE]">
            <span className="text-[#586D61]">Total tamu:</span>{" "}
            <span className="font-bold text-[#172E21]" data-testid="reminder-total-count">{filtered.length}</span>
          </div>
          <div className="px-3 py-1.5 rounded-lg bg-emerald-50 border border-emerald-200">
            <span className="text-emerald-700">Sudah dikirim:</span>{" "}
            <span className="font-bold text-emerald-800" data-testid="reminder-sent-count">{sentCount}</span>
          </div>
          <div className="px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200">
            <span className="text-amber-700">Belum:</span>{" "}
            <span className="font-bold text-amber-800" data-testid="reminder-pending-count">{filtered.length - sentCount}</span>
          </div>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="bg-white rounded-2xl border border-[#EBE6DE] p-12 flex items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-[#D16B4D]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#EBE6DE] p-12 text-center">
          <Bell className="h-12 w-12 mx-auto text-[#586D61]/40 mb-3" />
          <div className="font-heading text-xl text-[#172E21]">Tidak ada tamu untuk periode ini</div>
          <p className="text-sm text-[#586D61] mt-2">Coba ganti periode atau tambah booking baru.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="reminders-grid">
          {filtered.map((b) => {
            const sent = isSent(b.id, targetDate);
            const hasPhone = Boolean(b.phone_number);
            return (
              <div
                key={b.id}
                data-testid={`reminder-card-${b.id}`}
                className={`bg-white rounded-2xl border p-5 shadow-sm transition-all ${
                  sent ? "border-emerald-300 bg-emerald-50/30" : "border-[#EBE6DE] hover:border-[#D16B4D]/40 hover:shadow-md"
                }`}
              >
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-lg font-semibold text-[#172E21] truncate">{b.guest_name}</h3>
                      {sent && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider">
                          <CheckCircle2 className="h-3 w-3" />
                          Terkirim
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <StatusBadge status={b.status} />
                      <span className="text-xs text-[#586D61]">{b.platform}</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5 text-sm text-[#172E21] mb-4">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-[#D16B4D] flex-shrink-0" />
                    <span>Check-in: <strong>{formatDate(b.check_in)}</strong> ({b.nights} malam)</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <BedDouble className="h-4 w-4 text-[#D16B4D] flex-shrink-0" />
                    <span>{b.room_name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4 text-[#D16B4D] flex-shrink-0" />
                    <span>{b.guests} tamu</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-[#D16B4D] flex-shrink-0" />
                    {hasPhone ? (
                      <span className="font-mono text-xs">{b.phone_number}</span>
                    ) : (
                      <span className="text-amber-700 italic text-xs">Tidak ada nomor HP</span>
                    )}
                  </div>
                  {b.notes && (
                    <div className="flex items-start gap-2 mt-1 p-2 rounded-lg bg-amber-50 border border-amber-200">
                      <span className="text-amber-700 flex-shrink-0 text-xs font-bold uppercase tracking-wider">Catatan:</span>
                      <span className="text-xs text-amber-900">{b.notes}</span>
                    </div>
                  )}
                  <div className="text-xs text-[#586D61] pt-1">
                    Total: <strong className="text-[#D16B4D]">{formatIDR(b.total_price)}</strong>
                    {b.deposit > 0 && b.total_price - b.deposit > 0 && (
                      <span> · Sisa: {formatIDR(b.total_price - b.deposit)}</span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2">
                  <Button
                    data-testid={`btn-send-wa-${b.id}`}
                    onClick={() => handleSend(b)}
                    disabled={!hasPhone}
                    className="flex-1 bg-[#25D366] hover:bg-[#1FA855] text-white rounded-xl"
                  >
                    <MessageCircle className="h-4 w-4 mr-2" />
                    Kirim WA
                  </Button>
                  <Button
                    data-testid={`btn-toggle-sent-${b.id}`}
                    onClick={() => toggleSent(b)}
                    variant="outline"
                    className="rounded-xl border-[#EBE6DE]"
                    title={sent ? "Tandai belum dikirim" : "Tandai sudah dikirim"}
                  >
                    <CheckCircle2 className={`h-4 w-4 ${sent ? "text-emerald-600" : "text-[#586D61]"}`} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Help footer */}
      <div className="bg-[#FDFBF7] border border-[#EBE6DE] rounded-2xl p-5 text-sm text-[#586D61]">
        <div className="font-bold text-[#172E21] mb-1">Cara kerja Reminder H-1</div>
        <ul className="list-disc list-inside space-y-1 text-xs">
          <li>Klik tombol <strong className="text-[#25D366]">Kirim WA</strong> untuk membuka WhatsApp Web/App dengan pesan pengingat siap kirim.</li>
          <li>Status "Sudah dikirim" disimpan di browser ini — buka di HP/perangkat front desk yang sama untuk konsistensi.</li>
          <li>Gratis tanpa biaya API. Untuk auto-send terjadwal, aktifkan <em>WhatsApp Auto-Reminder</em> di Settings (butuh API berbayar).</li>
        </ul>
      </div>
    </div>
  );
}
